import { useCallback, useEffect, useRef, useState } from 'react';

import nbnaLogo from '../assets/nbna-canada-logo.png';
import {
  EMPTY_MEETING_WELCOME,
  formatMeetingWelcomeCurrency,
  formatMeetingWelcomeDate,
  getMeetingWelcome,
  normalizeMeetingWelcome,
} from '../lib/meetingWelcome';

const METRICS = [
  ['closedBusiness', 'Thank you for closed business'],
  ['yearToDate', 'Total for 2026 to date'],
  ['onTrack', 'On track for 2026'],
  ['dailyAverage', 'Current daily average'],
  ['averageSeatValue', 'Average seat value'],
];

export default function MeetingWelcome() {
  const [values, setValues] = useState(EMPTY_MEETING_WELCOME);
  const [status, setStatus] = useState('loading');
  const wakeLockRef = useRef(null);

  useEffect(() => {
    let active = true;
    getMeetingWelcome()
      .then((payload) => {
        if (active) {
          setValues(normalizeMeetingWelcome(payload));
          setStatus('ready');
        }
      })
      .catch(() => {
        if (active) setStatus('error');
      });
    return () => {
      active = false;
    };
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator) || document.visibilityState !== 'visible') return;
    if (wakeLockRef.current && !wakeLockRef.current.released) return;

    try {
      const lock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = lock;
      lock.addEventListener('release', () => {
        if (wakeLockRef.current === lock) wakeLockRef.current = null;
      }, { once: true });
    } catch {
      // Browser or device policy can decline the request. Retry silently below.
    }
  }, []);

  useEffect(() => {
    let active = true;
    const maintainWakeLock = () => {
      if (active && document.visibilityState === 'visible') requestWakeLock();
    };

    maintainWakeLock();
    document.addEventListener('visibilitychange', maintainWakeLock);
    const retryTimer = window.setInterval(maintainWakeLock, 30_000);

    return () => {
      active = false;
      window.clearInterval(retryTimer);
      document.removeEventListener('visibilitychange', maintainWakeLock);
      wakeLockRef.current?.release?.();
      wakeLockRef.current = null;
    };
  }, [requestWakeLock]);

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden bg-[#eef6fb] text-[#102b42]">
      <div className="mx-auto flex h-screen w-full max-w-[1600px] flex-col px-5 py-3 sm:px-9 lg:px-12 lg:py-4">
        <header>
          <img className="h-auto w-44 sm:w-52 lg:w-56" src={nbnaLogo} alt="Nanaimo Business Networking Association" />
        </header>

        {status === 'loading' ? (
          <div className="flex flex-1 items-center justify-center text-xl">Loading meeting screen…</div>
        ) : null}
        {status === 'error' ? (
          <div className="flex flex-1 items-center justify-center text-xl text-red-800">
            Last week&apos;s numbers are unavailable. Refresh this screen to try again.
          </div>
        ) : null}

        {status === 'ready' ? (
          <main className="grid min-h-0 flex-1 items-center gap-6 py-1 lg:grid-cols-[minmax(300px,0.8fr)_minmax(560px,1.5fr)] lg:gap-10">
            <section className="flex flex-col items-center text-center">
              <h1 className="font-serif text-[clamp(2.25rem,3.5vw,4rem)] font-semibold leading-[0.95] text-[#0E4069]">Welcome to NBNA</h1>
              <p className="mt-2 text-[clamp(1.05rem,1.7vw,1.5rem)] font-bold text-[#2d638d]">Scan to register for today&apos;s meeting</p>
              <div className="mt-4 rounded-[1.75rem] bg-white p-4 shadow-[0_18px_55px_rgba(14,64,105,0.16)]">
                <img
                  src="/meeting-registration-qr.png"
                  alt="NBNA meeting registration QR code"
                  className="h-auto w-[min(64vw,43vh,350px)]"
                />
              </div>
              <p className="mt-3 text-[clamp(0.72rem,1vw,1rem)] font-bold tracking-wide text-[#0E4069]">app.nanaimobusinessnetworking.com/meeting/register</p>
            </section>

            <section className="rounded-[2rem] bg-[#0E4069] px-7 py-6 text-white shadow-[0_22px_70px_rgba(14,64,105,0.24)] sm:px-9 lg:px-10">
              <p className="text-[clamp(0.85rem,1.4vw,1.15rem)] font-bold uppercase tracking-[0.2em] text-[#93cdf2]">
                {formatMeetingWelcomeDate(values.reportDate)}
              </p>
              <h2 className="mt-1 font-serif text-[clamp(2.35rem,3.8vw,4.5rem)] font-semibold leading-none text-white">Last week’s numbers</h2>

              <dl className="mt-4 divide-y divide-white/20">
                {METRICS.map(([key, label]) => (
                  <div key={key} className="flex flex-col gap-1 py-[clamp(0.45rem,1.15vh,0.85rem)] sm:flex-row sm:items-baseline sm:justify-between sm:gap-8">
                    <dt className="text-[clamp(0.98rem,1.5vw,1.3rem)] font-bold text-[#c6e4f7]">{label}</dt>
                    <dd className="text-[clamp(1.75rem,2.8vw,3.25rem)] font-bold leading-none tabular-nums">
                      {formatMeetingWelcomeCurrency(values[key])}
                    </dd>
                  </div>
                ))}
              </dl>

              <p className="mt-3 text-right text-[clamp(0.9rem,1.4vw,1.2rem)] font-bold text-[#c6e4f7]">
                Average seat value based on {values.memberCount} members
              </p>
            </section>
          </main>
        ) : null}
      </div>
    </div>
  );
}
